// JobFilterBar — the candidate job-board structured filter bar (#39, Phase 1).
// Composed entirely from reusable custom-ui primitives (InlineSelect, MultiSelect)
// so the styling matches the rest of the app. Single-value facets AND together;
// competencies OR within the facet. Renders a sort control + result count + a
// clearable active-filter chip row.
import React from 'react';
import { MapPin, Briefcase, Clock, Building2, Award, ShieldCheck, ArrowDownWideNarrow, X } from 'lucide-react';
import { InlineSelect, type InlineSelectOption } from '@/components/custom-ui/InlineSelect';
import { MultiSelect } from '@/components/custom-ui/MultiSelect';
import { EmploymentTypeLabels, EmploymentExtentLabels } from '@/lib/models/need';
import { type JobFilters, type JobSort, jobFiltersActive, EMPTY_JOB_FILTERS } from '@/lib/jobMatching';

interface JobFilterBarProps {
    filters: JobFilters;
    onFiltersChange: (f: JobFilters) => void;
    sort: JobSort;
    onSortChange: (s: JobSort) => void;
    cityOptions: string[];
    competencyOptions: string[];
    resultCount: number;
}

const withAll = (label: string, opts: InlineSelectOption[]): InlineSelectOption[] => [
    { value: '', label },
    ...opts,
];

const EMPLOYMENT_TYPE_OPTS: InlineSelectOption[] = (Object.keys(EmploymentTypeLabels) as (keyof typeof EmploymentTypeLabels)[])
    .map((v) => ({ value: v, label: EmploymentTypeLabels[v] }));
const EXTENT_OPTS: InlineSelectOption[] = (Object.keys(EmploymentExtentLabels) as (keyof typeof EmploymentExtentLabels)[])
    .map((v) => ({ value: v, label: EmploymentExtentLabels[v] }));
const WORK_LOCATION_OPTS: InlineSelectOption[] = [
    { value: 'onsite', label: 'På plats' },
    { value: 'remote', label: 'Distans' },
];
const SORT_OPTS: InlineSelectOption[] = [
    { value: 'recent', label: 'Senaste' },
    { value: 'deadline', label: 'Sista ansökningsdag' },
];

const JobFilterBar: React.FC<JobFilterBarProps> = ({
    filters,
    onFiltersChange,
    sort,
    onSortChange,
    cityOptions,
    competencyOptions,
    resultCount,
}) => {
    const set = (patch: Partial<JobFilters>) => onFiltersChange({ ...filters, ...patch });
    const active = jobFiltersActive(filters);

    // Active-filter chips (label + how to clear each one).
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (filters.city) chips.push({ key: 'city', label: filters.city, clear: () => set({ city: '' }) });
    if (filters.employmentType)
        chips.push({ key: 'type', label: EmploymentTypeLabels[filters.employmentType as keyof typeof EmploymentTypeLabels] ?? filters.employmentType, clear: () => set({ employmentType: '' }) });
    if (filters.extent)
        chips.push({ key: 'extent', label: EmploymentExtentLabels[filters.extent as keyof typeof EmploymentExtentLabels] ?? filters.extent, clear: () => set({ extent: '' }) });
    if (filters.workLocation)
        chips.push({ key: 'loc', label: filters.workLocation === 'onsite' ? 'På plats' : 'Distans', clear: () => set({ workLocation: '' }) });
    filters.competencies.forEach((c) =>
        chips.push({ key: `comp:${c}`, label: c, clear: () => set({ competencies: filters.competencies.filter((x) => x !== c) }) }),
    );
    if (filters.supervisionOnly)
        chips.push({ key: 'sup', label: 'Handledning erbjuds', clear: () => set({ supervisionOnly: false }) });

    return (
        <div className="mt-3 space-y-3">
            {/* Facet controls */}
            <div className="flex flex-wrap items-center gap-2">
                <InlineSelect
                    variant="outline"
                    icon={MapPin}
                    ariaLabel="Filtrera på ort"
                    value={filters.city}
                    onChange={(v) => set({ city: v })}
                    options={withAll('Alla orter', cityOptions.map((c) => ({ value: c, label: c })))}
                    className="w-40"
                />
                <InlineSelect
                    variant="outline"
                    icon={Briefcase}
                    ariaLabel="Filtrera på anställningsform"
                    value={filters.employmentType}
                    onChange={(v) => set({ employmentType: v })}
                    options={withAll('Alla anställningsformer', EMPLOYMENT_TYPE_OPTS)}
                    className="w-48"
                />
                <InlineSelect
                    variant="outline"
                    icon={Clock}
                    ariaLabel="Filtrera på omfattning"
                    value={filters.extent}
                    onChange={(v) => set({ extent: v })}
                    options={withAll('Alla omfattningar', EXTENT_OPTS)}
                    className="w-40"
                />
                <InlineSelect
                    variant="outline"
                    icon={Building2}
                    ariaLabel="Filtrera på arbetsplats"
                    value={filters.workLocation}
                    onChange={(v) => set({ workLocation: v })}
                    options={withAll('På plats & distans', WORK_LOCATION_OPTS)}
                    className="w-44"
                />
                <MultiSelect
                    icon={Award}
                    ariaLabel="Filtrera på kompetenser"
                    placeholder="Kompetenser"
                    values={filters.competencies}
                    onChange={(v) => set({ competencies: v })}
                    options={competencyOptions.map((c) => ({ value: c, label: c }))}
                    summarise={(n) => `${n} kompetenser`}
                    className="w-44"
                />
                <button
                    type="button"
                    onClick={() => set({ supervisionOnly: !filters.supervisionOnly })}
                    aria-pressed={filters.supervisionOnly}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        filters.supervisionOnly
                            ? 'border-[#1A4D4F] bg-[#A4C3B2]/30 text-[#1A4D4F] font-medium'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                >
                    <ShieldCheck className="w-3.5 h-3.5" /> Handledning
                </button>

                {/* Sort — pushed to the right */}
                <div className="ml-auto flex items-center gap-2">
                    <ArrowDownWideNarrow className="w-4 h-4 text-gray-400" />
                    <InlineSelect
                        variant="outline"
                        ariaLabel="Sortera"
                        value={sort}
                        onChange={(v) => onSortChange(v as JobSort)}
                        options={SORT_OPTS}
                        className="w-48"
                    />
                </div>
            </div>

            {/* Result count + active-filter chips */}
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">
                    {resultCount} {resultCount === 1 ? 'tjänst' : 'tjänster'}
                </span>
                {chips.map((chip) => (
                    <span
                        key={chip.key}
                        className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 bg-[#A4C3B2]/30 text-[#1A4D4F] rounded-full text-xs font-medium"
                    >
                        {chip.label}
                        <button
                            type="button"
                            onClick={chip.clear}
                            className="p-0.5 rounded-full hover:bg-[#1A4D4F]/10"
                            title="Ta bort filter"
                        >
                            <X className="w-3 h-3" />
                        </button>
                    </span>
                ))}
                {active && (
                    <button
                        type="button"
                        onClick={() => onFiltersChange(EMPTY_JOB_FILTERS)}
                        className="text-xs text-gray-500 hover:text-gray-800 underline underline-offset-2"
                    >
                        Rensa allt
                    </button>
                )}
            </div>
        </div>
    );
};

export default JobFilterBar;
